import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { users } from '../../services/api';
import LayoutV2 from '../../components/LayoutV2';
import {
  Badge,
  Button,
  Loading,
  PageHeader,
  SectionBlock,
  StatCard
} from '../../components/ui';

function validateGcashNumber(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  if (!/^09\d{9}$/.test(normalized)) {
    return 'GCash number must start with 09 and contain exactly 11 digits';
  }
  return '';
}

export default function UserDetailV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [downlines, setDownlines] = useState({ current: [], historical: [] });
  const [loading, setLoading] = useState(true);
  const [gcashForm, setGcashForm] = useState({ gcashName: '', gcashNumber: '' });
  const [gcashFeedback, setGcashFeedback] = useState({ type: '', text: '' });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [userRes, downlinesRes, gcashRes] = await Promise.all([
          users.get(id),
          users.getDownlines(id),
          users.getGcash(id)
        ]);
        setUser(userRes.data.user);
        setGcashForm({
          gcashName: String(gcashRes.data.gcashName || ''),
          gcashNumber: String(gcashRes.data.gcashNumber || '')
        });
        setDownlines(downlinesRes.data || { current: [], historical: [] });
      } catch (err) {
        console.error(err);
        navigate('/admin/users');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate]);

  const tierNames = { 0: 'Admin', 1: 'Director', 2: 'Manager', 3: 'Promoter' };

  const saveGcash = async () => {
    const gcashNumberError = validateGcashNumber(gcashForm.gcashNumber);
    if (gcashNumberError) {
      setGcashFeedback({ type: 'error', text: gcashNumberError });
      return;
    }

    try {
      await users.updateGcash(id, gcashForm);
      setGcashFeedback({ type: 'success', text: 'GCash saved successfully.' });
    } catch (err) {
      setGcashFeedback({
        type: 'error',
        text: err.response?.data?.error || 'Failed to save GCash'
      });
    }
  };

  if (loading) return <LayoutV2><Loading text="Loading user..." /></LayoutV2>;
  if (!user) return <LayoutV2><div className="text-sm text-slate-400">User not found.</div></LayoutV2>;

  return (
    <LayoutV2>
      <PageHeader
        eyebrow="Admin Detail"
        title={user.display_name}
        description="Inspect profile metadata, payment details, and current or historical downline relationships."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="primary">{tierNames[user.tier]}</Badge>
            <Button type="button" variant="ghost" onClick={() => navigate('/admin/users')}>Back</Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <StatCard label="Username" value={`@${user.username}`} tone="primary" />
        <StatCard label="Status" value={user.status} tone="neutral" />
        <StatCard label="Referral Code" value={user.inviteCode || '--'} tone="success" />
        <StatCard label="Current Downlines" value={String(downlines.current?.length || 0)} tone="warning" />
      </div>

      <div className="mt-6 space-y-6">
        <SectionBlock eyebrow="Profile" title="User summary">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="surface-card"><p className="label">Display Name</p><p className="text-sm text-slate-100">{user.display_name}</p></div>
            <div className="surface-card"><p className="label">Region</p><p className="text-sm text-slate-100">{user.region || '-'}</p></div>
            <div className="surface-card"><p className="label">GCash</p><p className="text-sm text-slate-100">{user.gcashName || '--'} {user.gcashNumber ? `(${user.gcashNumber})` : ''}</p></div>
            <div className="surface-card"><p className="label">Created At</p><p className="text-sm text-slate-100">{new Date(user.created_at).toLocaleString()}</p></div>
          </div>
        </SectionBlock>

        <SectionBlock eyebrow="Payout" title="GCash account">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <input value={gcashForm.gcashName} onChange={(e) => {
              setGcashForm((prev) => ({ ...prev, gcashName: e.target.value }));
              setGcashFeedback({ type: '', text: '' });
            }} placeholder="Account name" className="input" />
            <input value={gcashForm.gcashNumber} onChange={(e) => {
              setGcashForm((prev) => ({ ...prev, gcashNumber: e.target.value }));
              setGcashFeedback({ type: '', text: '' });
            }} placeholder="Account number / phone" className="input" />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button type="button" variant="primary" onClick={saveGcash}>Save GCash</Button>
            {gcashFeedback.text ? <p className={`text-sm ${gcashFeedback.type === 'success' ? 'text-emerald-200' : 'text-red-200'}`}>{gcashFeedback.text}</p> : null}
          </div>
        </SectionBlock>

        <SectionBlock eyebrow="Hierarchy" title={`Current Downlines (${downlines.current?.length || 0})`}>
          {downlines.current?.length ? (
            <div className="grid grid-cols-1 gap-3">
              {downlines.current.map((d) => (
                <div key={d.id} className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-sm font-medium text-slate-100">{d.display_name} @{d.username}</p>
                  <p className="mt-1 text-sm text-slate-400">{tierNames[d.tier]} · {d.status}</p>
                  <p className="mt-1 text-sm text-slate-400">Referral Code: <span className="font-mono text-slate-200">{d.inviteCode || '--'}</span></p>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-slate-400">No current downlines.</div>}
        </SectionBlock>

        <SectionBlock eyebrow="History" title={`Historical Downlines (${downlines.historical?.length || 0})`}>
          {downlines.historical?.length ? (
            <div className="grid grid-cols-1 gap-3">
              {downlines.historical.map((d) => (
                <div key={d.id} className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-sm font-medium text-slate-100">{d.display_name} @{d.username}</p>
                  <p className="mt-1 text-sm text-slate-400">Moved: {new Date(d.moved_at).toLocaleDateString()} to {d.moved_to || '-'}</p>
                  <p className="mt-1 text-sm text-slate-400">Referral Code: <span className="font-mono text-slate-200">{d.inviteCode || '--'}</span></p>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-slate-400">No historical downlines.</div>}
        </SectionBlock>
      </div>
    </LayoutV2>
  );
}
